import { useCallback, useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { Activity, Clock, Inbox, Loader2, Send } from "lucide-react";

import {
  AXIS_COLOR,
  ChartCard,
  EmptyState,
  formatNumber,
  gridColor,
  makeTooltip,
  seriesColor
} from "../Dashboard/charts";
import { Card, CardContent } from "../../components/ui/card";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useThemeContext } from "../../context/DarkMode";
import { nomeDaRede } from "./redes";
import Cabecalho from "./Cabecalho";

// Como anda a conversa nas redes: volume por dia, por rede e tempo de
// resposta. Os números vêm prontos da Zernio — recontar aqui daria uma
// segunda verdade, que discordaria da dela na primeira mensagem perdida.

const KPI = ({ icone: Icone, rotulo, valor, detalhe }) => (
  <Card>
    <CardContent className="flex items-center gap-3 p-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Icone className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{rotulo}</p>
        <p className="text-xl font-semibold tabular-nums">{valor}</p>
        {detalhe && (
          <p className="text-xs text-muted-foreground">{detalhe}</p>
        )}
      </div>
    </CardContent>
  </Card>
);

const diaCurto = (iso) => {
  if (!iso) return "";

  // A data vem como "aaaa-mm-dd": `new Date` nesse formato é meia-noite UTC e
  // em fuso negativo mostraria o dia anterior.
  const [ano, mes, dia] = String(iso).split("-").map(Number);

  return new Date(ano, mes - 1, dia).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit"
  });
};

const minutos = (valor) => {
  if (valor === null || valor === undefined) return "—";

  const num = Number(valor);

  if (!Number.isFinite(num)) return "—";
  if (num < 60) return `${Math.round(num)} min`;

  return `${(num / 60).toFixed(1)} h`;
};

const Analises = () => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);

  const { darkMode } = useThemeContext();

  const azul = seriesColor("blue", darkMode);
  const verde = seriesColor("aqua", darkMode);
  const grade = gridColor(darkMode);

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      const { data } = await api.get("/zernio/analytics");
      setDados(data);
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (carregando) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const resumo = dados?.volume?.summary || {};
  const serie = (dados?.volume?.timeseries || []).map((ponto) => ({
    dia: diaCurto(ponto.date),
    Recebidas: ponto.received || 0,
    Enviadas: ponto.sent || 0
  }));

  const porRede = (dados?.volume?.byPlatform || []).map((linha) => ({
    rede: nomeDaRede(linha.platform),
    Total: (linha.received || 0) + (linha.sent || 0)
  }));

  const tempoMedio =
    dados?.tempoResposta?.summary?.averageMinutes ??
    dados?.tempoResposta?.averageMinutes ??
    null;

  return (
    <div className="space-y-4 p-4">
      <Cabecalho
        titulo="Análises"
        descricao={`Como anda a conversa nas redes, de ${diaCurto(
          dados?.fromDate
        )} até ${dados?.toDate ? diaCurto(dados.toDate) : "hoje"}.`}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KPI
          icone={Inbox}
          rotulo="Recebidas"
          valor={formatNumber(resumo.received || 0)}
        />
        <KPI
          icone={Send}
          rotulo="Enviadas"
          valor={formatNumber(resumo.sent || 0)}
          detalhe={
            resumo.failed ? `${formatNumber(resumo.failed)} falharam` : null
          }
        />
        <KPI
          icone={Activity}
          rotulo="Conversas"
          valor={formatNumber(resumo.uniqueConversations || 0)}
        />
        <KPI
          icone={Clock}
          rotulo="Tempo de resposta"
          valor={minutos(tempoMedio)}
        />
      </div>

      <ChartCard
        title="Mensagens por dia"
        description="Recebidas e enviadas em todas as redes conectadas."
        icon={Activity}
      >
        {serie.length === 0 ? (
          <EmptyState />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={serie} margin={{ top: 8, right: 8, bottom: 0 }}>
              <CartesianGrid stroke={grade} vertical={false} />
              <XAxis
                dataKey="dia"
                stroke={AXIS_COLOR}
                tickLine={false}
                axisLine={false}
                fontSize={11}
              />
              <YAxis
                stroke={AXIS_COLOR}
                tickLine={false}
                axisLine={false}
                fontSize={11}
                allowDecimals={false}
              />
              <Tooltip content={makeTooltip()} />
              {/* Duas séries: a legenda é obrigatória para a identidade não
                  depender só da cor. */}
              <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="Recebidas"
                stroke={azul}
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="Enviadas"
                stroke={verde}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard
        title="Volume por rede"
        description="Total de mensagens trocadas em cada rede no período."
        icon={Inbox}
      >
        {porRede.length === 0 ? (
          <EmptyState />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(160, porRede.length * 44)}>
            <BarChart
              data={porRede}
              layout="vertical"
              margin={{ top: 4, right: 40, bottom: 4, left: 8 }}
            >
              <CartesianGrid stroke={grade} horizontal={false} />
              <XAxis type="number" hide allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="rede"
                stroke={AXIS_COLOR}
                tickLine={false}
                axisLine={false}
                width={110}
                fontSize={12}
              />
              <Tooltip content={makeTooltip()} cursor={false} />
              {/* Série única: sem legenda, com o valor escrito na ponta — é o
                  rótulo visível que a cor de contraste mais baixo exige. */}
              <Bar dataKey="Total" fill={azul} radius={[0, 4, 4, 0]} barSize={18}>
                <LabelList
                  dataKey="Total"
                  position="right"
                  fontSize={11}
                  fill={AXIS_COLOR}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
};

export default Analises;
